import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Stack,
  MenuItem,
  Divider,
  Tab,
  Tabs,
  Avatar,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  Fade,
  Zoom,
} from "@mui/material";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import EventSeatIcon from "@mui/icons-material/EventSeat";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import GavelIcon from "@mui/icons-material/Gavel";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SettingsIcon from "@mui/icons-material/Settings";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import NotesIcon from "@mui/icons-material/Notes";
import { useNotify } from "react-admin";
import { useNavigate } from "react-router-dom";

const API = "https://api.haymestasarim.com/api";

/* ================= Renk Paleti ================= */
const COLORS = {
  primary: "#1976d2",
  secondary: "#f57c00",
  success: "#2e7d32",
  warning: "#ed6c02",
  error: "#d32f2f",
  info: "#0288d1",
  background: {
    light: "#f5f5f5",
    dark: "#333333",
  },
};

/* ================= I18N ================= */
const I18N = {
  tr: {
    title: "Randevu Yönetimi",
    desc: "Tüm randevuları tek panelden yönetin.",
    selectDate: "Tarih Seç",
    capacity: "Kapasite",
    table: "Masa",
    doctor: "Doktor",
    lawyer: "Avukat",
    consultant: "Danışman",
    status: "Durum",
    all: "Tümü",
    available: "Müsait",
    reserved: "Rezerve",
    completed: "Tamamlandı",
    pending: "Onay Bekliyor",
    approved: "Onaylandı",
    rejected: "Reddedildi",
    cancelled: "İptal Edildi",
    close: "Kapat",
    loading: "Yükleniyor...",
    empty: "Kayıt bulunamadı.",
    statsTotal: "Toplam",
    statsAvailable: "Müsait",
    statsReserved: "Rezerve",
    statsCompleted: "Tamamlanan",
    statsPending: "Bekleyen",
    restaurant: "Restoran",
    healthcare: "Sağlık",
    legal: "Hukuk",
    finance: "Finans",
    appointmentTime: "Randevu Saati",
    duration: "Süre",
    minutes: "dk",
    customer: "Müşteri",
    notes: "Notlar",
    approve: "Onayla",
    reject: "Reddet",
    cancel: "İptal Et",
    complete: "Tamamla",
    refresh: "Yenile",
    details: "Detaylar",
    today: "Bugün",
    tomorrow: "Yarın",
    thisWeek: "Bu Hafta",
    filters: "Filtreler",
    clearFilters: "Filtreleri Temizle",
    selectResource: "Kaynak Seç",
    allResources: "Tüm Kaynaklar",
  },
  en: {
    title: "Appointment Management",
    desc: "Manage all appointments from one panel.",
    selectDate: "Select Date",
    capacity: "Capacity",
    table: "Table",
    doctor: "Doctor",
    lawyer: "Lawyer",
    consultant: "Consultant",
    status: "Status",
    all: "All",
    available: "Available",
    reserved: "Reserved",
    completed: "Completed",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    cancelled: "Cancelled",
    close: "Close",
    loading: "Loading...",
    empty: "No records found.",
    statsTotal: "Total",
    statsAvailable: "Available",
    statsReserved: "Reserved",
    statsCompleted: "Completed",
    statsPending: "Pending",
    restaurant: "Restaurant",
    healthcare: "Healthcare",
    legal: "Legal",
    finance: "Finance",
    appointmentTime: "Appointment Time",
    duration: "Duration",
    minutes: "min",
    customer: "Customer",
    notes: "Notes",
    approve: "Approve",
    reject: "Reject",
    cancel: "Cancel",
    complete: "Complete",
    refresh: "Refresh",
    details: "Details",
    today: "Today",
    tomorrow: "Tomorrow",
    thisWeek: "This Week",
    filters: "Filters",
    clearFilters: "Clear Filters",
    selectResource: "Select Resource",
    allResources: "All Resources",
  },
  de: {
    title: "Terminverwaltung",
    desc: "Verwalten Sie alle Termine von einem Panel aus.",
    selectDate: "Datum auswählen",
    capacity: "Kapazität",
    table: "Tisch",
    doctor: "Arzt",
    lawyer: "Anwalt",
    consultant: "Berater",
    status: "Status",
    all: "Alle",
    available: "Frei",
    reserved: "Reserviert",
    completed: "Abgeschlossen",
    pending: "Ausstehend",
    approved: "Genehmigt",
    rejected: "Abgelehnt",
    cancelled: "Storniert",
    close: "Schließen",
    loading: "Laden...",
    empty: "Keine Einträge gefunden.",
    statsTotal: "Gesamt",
    statsAvailable: "Frei",
    statsReserved: "Reserviert",
    statsCompleted: "Abgeschlossen",
    statsPending: "Ausstehend",
    restaurant: "Restaurant",
    healthcare: "Gesundheit",
    legal: "Recht",
    finance: "Finanzen",
    appointmentTime: "Terminzeit",
    duration: "Dauer",
    minutes: "Min",
    customer: "Kunde",
    notes: "Notizen",
    approve: "Genehmigen",
    reject: "Ablehnen",
    cancel: "Stornieren",
    complete: "Abschließen",
    refresh: "Aktualisieren",
    details: "Details",
    today: "Heute",
    tomorrow: "Morgen",
    thisWeek: "Diese Woche",
    filters: "Filter",
    clearFilters: "Filter löschen",
    selectResource: "Ressource wählen",
    allResources: "Alle Ressourcen",
  },
};

function getLang() {
  const l = localStorage.getItem("app_lang") || "tr";
  return ["tr", "en", "de"].includes(l) ? l : "tr";
}

function authHeaders() {
  return {
    Authorization: "Bearer " + localStorage.getItem("token"),
    "Content-Type": "application/json",
  };
}

const STATUS_COLORS = {
  available: "success",
  reserved: "warning",
  completed: "success",
  pending: "warning",
  approved: "info",
  rejected: "error",
  cancelled: "default",
};

const STATUS_ICONS = {
  available: <CheckCircleIcon fontSize="small" />,
  reserved: <PendingIcon fontSize="small" />,
  pending: <PendingIcon fontSize="small" />,
  approved: <CheckCircleIcon fontSize="small" />,
  completed: <CheckCircleIcon fontSize="small" />,
  rejected: <CancelIcon fontSize="small" />,
  cancelled: <CancelIcon fontSize="small" />,
};

const BUSINESS_TYPES = [
  { 
    id: "restaurant", 
    icon: <TableRestaurantIcon />, 
    label: "restaurant", 
    color: COLORS.secondary,
    bgColor: "#fff3e0",
  },
  { 
    id: "healthcare", 
    icon: <MedicalServicesIcon />, 
    label: "healthcare", 
    color: COLORS.success,
    bgColor: "#e8f5e8",
  },
  { 
    id: "legal", 
    icon: <GavelIcon />, 
    label: "legal", 
    color: COLORS.primary,
    bgColor: "#e3f2fd",
  },
  { 
    id: "finance", 
    icon: <AccountBalanceIcon />, 
    label: "finance", 
    color: "#9c27b0",
    bgColor: "#f3e5f5",
  },
];

function StatCard({ label, value, icon, color }) {
  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar sx={{ bgcolor: color, color: "white" }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h5" fontWeight={900}>
              {value}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function AppointmentCard({ item, businessType, T, onClick, STATUS_COLORS, STATUS_ICONS, activeColor }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: "pointer",
        borderRadius: 2,
        height: "100%",
        transition: "all 0.3s",
        border: `1px solid ${activeColor}20`,
        "&:hover": {
          boxShadow: `0 8px 16px ${activeColor}40`,
          transform: "translateY(-4px)",
          border: `1px solid ${activeColor}`,
        },
      }}
    >
      <CardContent>
        <Typography fontWeight={900} gutterBottom sx={{ color: activeColor }}>
          {businessType === "restaurant" 
            ? `${T.table} ${item.name}`
            : item.resource_adi || "Randevu"}
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <AccessTimeIcon fontSize="small" sx={{ color: activeColor }} />
          <Typography variant="body2">
            {item.baslangic || "00:00"} - {item.bitis || "00:00"}
          </Typography>
        </Stack>

        {businessType === "restaurant" && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <EventSeatIcon fontSize="small" sx={{ color: activeColor }} />
            <Typography variant="body2">
              {T.capacity}: {item.capacity}
            </Typography>
          </Stack>
        )}

        <Chip
          size="small"
          icon={STATUS_ICONS[item.status]}
          label={T[item.status] || item.status}
          color={STATUS_COLORS[item.status] || "default"}
          sx={{ mt: 1 }}
        />

        {item.kullanici_notu && (
          <Tooltip title={item.kullanici_notu}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mt: 1,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              📝 {item.kullanici_notu}
            </Typography>
          </Tooltip>
        )}
      </CardContent>
    </Card>
  );
}

export default function RestaurantTablesPage() {
  const notify = useNotify();
  const navigate = useNavigate();
  const T = I18N[getLang()];

  const [businessType, setBusinessType] = useState("restaurant");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [appointments, setAppointments] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [resourceFilter, setResourceFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [quickDate, setQuickDate] = useState("today");

  const fetchData = async () => {
    try {
      setLoading(true);
      
      if (businessType !== "restaurant") {
        try {
          const resourcesRes = await fetch(`${API}/randevu/resources?tip=${businessType}`, {
            headers: authHeaders(),
          });
          const resourcesJson = await resourcesRes.json();
          setResources(resourcesJson?.data || []);
        } catch (e) {
          setResources([]);
        }
      }

      let endpoint = "";
      if (businessType === "restaurant") {
        endpoint = `${API}/randevu/restaurant/tables/status?date=${date}`;
      } else {
        endpoint = `${API}/randevu/bookings?date=${date}`;
      }

      const appRes = await fetch(endpoint, {
        headers: authHeaders(),
      });
      const appJson = await appRes.json();
      setAppointments(appJson?.data || []);
    } catch (error) {
      notify("Veriler alınamadı", { type: "error" });
      setAppointments([]);
      setResources([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [businessType, date]);

  const handleQuickDate = (type) => {
    const today = new Date();
    if (type === "today") {
      setDate(today.toISOString().slice(0, 10));
    } else if (type === "tomorrow") {
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      setDate(tomorrow.toISOString().slice(0, 10));
    } else if (type === "thisWeek") {
      const monday = new Date(today);
      const day = today.getDay();
      const diff = day === 0 ? 6 : day - 1;
      monday.setDate(today.getDate() - diff);
      setDate(monday.toISOString().slice(0, 10));
    }
    setQuickDate(type);
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setResourceFilter("all");
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const stats = useMemo(() => {
    const list = Array.isArray(appointments) ? appointments : [];
    return {
      total: list.length,
      available: list.filter((a) => a.status === "available").length,
      reserved: list.filter((a) => ["reserved", "approved", "pending"].includes(a.status)).length,
      completed: list.filter((a) => a.status === "completed").length,
      pending: list.filter((a) => a.status === "pending").length,
    };
  }, [appointments]);

  const filtered = useMemo(() => {
    let filtered = Array.isArray(appointments) ? appointments : [];

    if (statusFilter !== "all") {
      filtered = filtered.filter((a) => a.status === statusFilter);
    }

    if (resourceFilter !== "all" && businessType !== "restaurant") {
      filtered = filtered.filter((a) => a.resource_id === parseInt(resourceFilter));
    }

    return filtered;
  }, [appointments, statusFilter, resourceFilter, businessType]);

  const groupedItems = useMemo(() => {
    const groups = {};

    if (businessType === "restaurant") {
      for (const item of filtered) {
        const key = item.salon || "Diğer";
        if (!groups[key]) groups[key] = [];
        groups[key].push(item);
      }
    } else {
      for (const item of filtered) {
        const resourceId = item.resource_id || "default";
        const resourceName = item.resource_adi || 
          resources.find(r => r.id === item.resource_id)?.ad || 
          "Genel";
        
        if (!groups[resourceId]) {
          groups[resourceId] = {
            name: resourceName,
            items: [],
          };
        }
        groups[resourceId].items.push(item);
      }
    }
    return groups;
  }, [filtered, resources, businessType]);

  const updateStatus = async (id, newStatus) => {
    try {
      const res = await fetch(`${API}/randevu/bookings/${id}/${newStatus}`, {
        method: "POST",
        headers: authHeaders(),
      });
      
      if (res.ok) {
        notify(`Randevu ${T[newStatus] || newStatus}`, { type: "success" });
        fetchData();
        setDetailsOpen(false);
      }
    } catch (error) {
      notify("İşlem başarısız", { type: "error" });
    }
  };

  const goToSettings = () => {
   navigate("/ayarlar/restoran");
  };

  const getActiveColor = () => {
    const biz = BUSINESS_TYPES.find(b => b.id === businessType);
    return biz?.color || COLORS.primary;
  };

  return (
    <Box sx={{ width: "100%", px: { xs: 1, md: 3 }, pb: 6 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2, 
          mb: 3, 
          background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
          color: "white",
          borderRadius: 2,
        }}
      >
        <Stack 
          direction={{ xs: "column", md: "row" }} 
          justifyContent="space-between" 
          alignItems="center"
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={900} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {BUSINESS_TYPES.find(b => b.id === businessType)?.icon}
              {T.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {T.desc}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title={T.refresh}>
              <IconButton 
                onClick={handleRefresh} 
                sx={{ 
                  color: "white", 
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Button
              startIcon={<SettingsIcon />}
              onClick={goToSettings}
              sx={{
                bgcolor: "white",
                color: getActiveColor(),
                px: 3,
                py: 1,
                fontWeight: 700,
                borderRadius: 2,
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.9)",
                },
              }}
            >
              {T.details}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Tabs
        value={businessType}
        onChange={(e, val) => setBusinessType(val)}
        sx={{ 
          mb: 3, 
          borderBottom: 1, 
          borderColor: "divider",
        }}
      >
        {BUSINESS_TYPES.map((type) => (
          <Tab
            key={type.id}
            value={type.id}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar 
                  sx={{ 
                    bgcolor: type.bgColor, 
                    color: type.color,
                    width: 36, 
                    height: 36,
                  }}
                >
                  {type.icon}
                </Avatar>
                <Stack alignItems="flex-start">
                  <Typography variant="body2" color="text.secondary">
                    {T[type.label]}
                  </Typography>
                  <Typography variant="h6" fontWeight={900}>
                    {stats.total}
                  </Typography>
                </Stack>
              </Stack>
            }
          />
        ))}
      </Tabs>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr 1fr",
            sm: "repeat(3, 1fr)",
            md: "repeat(5, 1fr)",
          },
          gap: 2,
          mb: 4,
        }}
      >
        <StatCard label={T.statsTotal} value={stats.total} icon={<CalendarTodayIcon />} color={COLORS.primary} />
        <StatCard label={T.statsAvailable} value={stats.available} icon={<CheckCircleIcon />} color={COLORS.success} />
        <StatCard label={T.statsReserved} value={stats.reserved} icon={<PendingIcon />} color={COLORS.warning} />
        <StatCard label={T.statsCompleted} value={stats.completed} icon={<CheckCircleIcon />} color={COLORS.info} />
        <StatCard label={T.statsPending} value={stats.pending} icon={<AccessTimeIcon />} color={COLORS.error} />
      </Box>

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={quickDate === "today" ? "contained" : "outlined"}
                onClick={() => handleQuickDate("today")}
                sx={{
                  bgcolor: quickDate === "today" ? getActiveColor() : "transparent",
                  borderColor: getActiveColor(),
                  color: quickDate === "today" ? "white" : getActiveColor(),
                }}
              >
                {T.today}
              </Button>
              <Button
                size="small"
                variant={quickDate === "tomorrow" ? "contained" : "outlined"}
                onClick={() => handleQuickDate("tomorrow")}
                sx={{
                  bgcolor: quickDate === "tomorrow" ? getActiveColor() : "transparent",
                  borderColor: getActiveColor(),
                  color: quickDate === "tomorrow" ? "white" : getActiveColor(),
                }}
              >
                {T.tomorrow}
              </Button>
              <Button
                size="small"
                variant={quickDate === "thisWeek" ? "contained" : "outlined"}
                onClick={() => handleQuickDate("thisWeek")}
                sx={{
                  bgcolor: quickDate === "thisWeek" ? getActiveColor() : "transparent",
                  borderColor: getActiveColor(),
                  color: quickDate === "thisWeek" ? "white" : getActiveColor(),
                }}
              >
                {T.thisWeek}
              </Button>
            </Stack>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
              <TextField
                type="date"
                label={T.selectDate}
                value={date}
                onChange={(e) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ minWidth: 200 }}
              />

              {businessType !== "restaurant" && (
                <TextField
                  select
                  label={T.selectResource}
                  value={resourceFilter}
                  onChange={(e) => setResourceFilter(e.target.value)}
                  size="small"
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="all">{T.allResources}</MenuItem>
                  {resources.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PersonIcon fontSize="small" />
                        <span>{r.ad}</span>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <TextField
                select
                label={T.status}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                size="small"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">{T.all}</MenuItem>
                <MenuItem value="available">{T.available}</MenuItem>
                <MenuItem value="reserved">{T.reserved}</MenuItem>
                <MenuItem value="pending">{T.pending}</MenuItem>
                <MenuItem value="approved">{T.approved}</MenuItem>
                <MenuItem value="completed">{T.completed}</MenuItem>
                <MenuItem value="rejected">{T.rejected}</MenuItem>
                <MenuItem value="cancelled">{T.cancelled}</MenuItem>
              </TextField>

              {(statusFilter !== "all" || resourceFilter !== "all") && (
                <Button variant="text" onClick={clearFilters} sx={{ color: COLORS.secondary }}>
                  {T.clearFilters}
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
          <CircularProgress sx={{ color: getActiveColor() }} />
          <Typography sx={{ mt: 2 }} color="text.secondary">
            {T.loading}
          </Typography>
        </Box>
      ) : filtered.length === 0 ? (
        <Fade in={true}>
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {T.empty}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {T.selectDate} {T.filters}
            </Typography>
          </Paper>
        </Fade>
      ) : (
        <Box>
          {Object.entries(groupedItems).map(([key, value], index) => (
            <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }} key={key}>
              <Box sx={{ mb: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    fontWeight={900}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: getActiveColor(),
                      borderLeft: `4px solid ${getActiveColor()}`,
                      pl: 2,
                    }}
                  >
                    {businessType === "restaurant" ? "🏠" : <PersonIcon />}
                    {businessType === "restaurant" ? key : value.name}
                    <Badge
                      badgeContent={value.items?.length || value.length}
                      sx={{
                        "& .MuiBadge-badge": {
                          bgcolor: getActiveColor(),
                          color: "white",
                        },
                      }}
                    />
                  </Typography>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  {(value.items || value).map((item) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                      <AppointmentCard
                        item={item}
                        businessType={businessType}
                        T={T}
                        onClick={() => {
                          setSelectedItem(item);
                          setDetailsOpen(true);
                        }}
                        STATUS_COLORS={STATUS_COLORS}
                        STATUS_ICONS={STATUS_ICONS}
                        activeColor={getActiveColor()}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Zoom>
          ))}
        </Box>
      )}

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: getActiveColor(), color: "white" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {BUSINESS_TYPES.find(b => b.id === businessType)?.icon}
            <Typography variant="h6">
              {businessType === "restaurant" ? `${T.table} ${selectedItem?.name}` : T.details}
            </Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedItem && (
            <Stack spacing={3}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: COLORS.background.light }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: getActiveColor(), color: "white" }}>
                    <AccessTimeIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {T.appointmentTime}
                    </Typography>
                    <Typography variant="h6">
                      {selectedItem.baslangic || "00:00"} - {selectedItem.bitis || "00:00"}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {T.status}
                </Typography>
                <Chip
                  icon={STATUS_ICONS[selectedItem.status]}
                  label={T[selectedItem.status] || selectedItem.status}
                  color={STATUS_COLORS[selectedItem.status] || "default"}
                  size="medium"
                />
              </Box>

              {businessType === "restaurant" && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {T.capacity}
                  </Typography>
                  <Typography variant="h6">
                    <EventSeatIcon sx={{ mr: 1, verticalAlign: "middle", color: getActiveColor() }} />
                    {selectedItem.capacity} {T.capacity}
                  </Typography>
                </Box>
              )}

              {businessType !== "restaurant" && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {businessType === "healthcare" ? T.doctor : businessType === "legal" ? T.lawyer : T.consultant}
                  </Typography>
                  <Typography variant="h6">
                    <PersonIcon sx={{ mr: 1, verticalAlign: "middle", color: getActiveColor() }} />
                    {selectedItem.resource_adi || "Belirtilmemiş"}
                  </Typography>
                </Box>
              )}

              {selectedItem.kullanici_notu && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <NotesIcon sx={{ mr: 0.5, verticalAlign: "middle" }} />
                    {T.customer} {T.notes}
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: COLORS.background.light }}>
                    <Typography>{selectedItem.kullanici_notu}</Typography>
                  </Paper>
                </Box>
              )}

              {selectedItem.kurum_notu && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <NotesIcon sx={{ mr: 0.5, verticalAlign: "middle" }} />
                    Kurum Notu
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: COLORS.background.light }}>
                    <Typography>{selectedItem.kurum_notu}</Typography>
                  </Paper>
                </Box>
              )}

              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                {selectedItem.status === "pending" && (
                  <>
                    <Button
                      variant="contained"
                      sx={{ bgcolor: COLORS.success }}
                      onClick={() => updateStatus(selectedItem.id, "approve")}
                    >
                      {T.approve}
                    </Button>
                    <Button
                      variant="contained"
                      sx={{ bgcolor: COLORS.error }}
                      onClick={() => updateStatus(selectedItem.id, "reject")}
                    >
                      {T.reject}
                    </Button>
                  </>
                )}

                {selectedItem.status === "approved" && (
                  <>
                    <Button
                      variant="contained"
                      sx={{ bgcolor: COLORS.success }}
                      onClick={() => updateStatus(selectedItem.id, "complete")}
                    >
                      {T.complete}
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ color: COLORS.error, borderColor: COLORS.error }}
                      onClick={() => updateStatus(selectedItem.id, "cancel")}
                    >
                      {T.cancel}
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailsOpen(null)} variant="outlined">
            {T.close}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}